import Helper from '../../Helper';
import { mediaClick as publisherMediaClick, thumbnailClick } from '../../../publisher';

const magnifyLargeImage = (largeImage, highResImage) => {
  return {
    src: largeImage,
    width: highResImage ? 2000 : 1000,
    height: highResImage ? 2000 : 1000
  };
};

export const getButtonTitle = (isLast, isInteractiveMedia, thumbnail, thumbnailCount) => {
  if (isLast) {
    return `View all ${thumbnailCount} media`;
  }
  if (isInteractiveMedia) {
    return `View ${thumbnail?.type?.toLowerCase()}`;
  }
  return 'View image';
};

export const getImageMagnifyProps = ({
  smallImageAlt,
  url,
  mainImageFetchPriority,
  largeImage,
  highResImage,
  isFluidWidth = true,
  isConfigurableS3 = false,
  enlargedImagePosition = 'beside'
}) => {
  return {
    smallImage: {
      alt: smallImageAlt,
      isFluidWidth,
      isConfigurableS3,
      src: url
    },
    largeImage: magnifyLargeImage(largeImage, highResImage),
    lensStyle: {
      backgroundImage: ''
    },
    enlargedImageContainerClassName:
      'sui-bg-primary sui-border sui-border-solid sui-border-1 '
      + 'sui-border-primary sui-z-tooltip',
    enlargedImageContainerDimensions: {
      width: '100%',
      height: '100%'
    },
    fetchPriority: mainImageFetchPriority,
    enlargedImagePosition
  };
};

export const getMediaEventProps = (
  grid,
  onThumbnailEvent,
  availableThumbnails,
  mainImageList,
  thumbnailAsset
) => {
  const baseOnClick = () => onThumbnailEvent(
    availableThumbnails,
    mainImageList,
    thumbnailAsset,
    thumbnailAsset,
    'click'
  );

  if (grid) {
    return {
      onClick: baseOnClick
    };
  }

  return {
    onClick: baseOnClick,
    onMouseEnter: () => {
      onThumbnailEvent(
        availableThumbnails,
        mainImageList,
        thumbnailAsset,
        thumbnailAsset,
        'mouseenter'
      );
    },
    onMouseLeave: () => {
      onThumbnailEvent(
        availableThumbnails,
        mainImageList,
        thumbnailAsset,
        thumbnailAsset,
        'mouseout'
      );
    }
  };
};

export const getOriginalIndexFromOrganized = (organizedIndex, organizedThumbs, originalThumbs) => {
  if (organizedIndex < 0 || organizedIndex >= organizedThumbs.length) return 0;

  const organizedThumbnail = organizedThumbs[organizedIndex];
  const originalIndex = originalThumbs.findIndex((t) => t.id === organizedThumbnail.id);
  return originalIndex !== -1 ? originalIndex : 0;
};

export function getThumbnailMeta(thumbnail, thumbnailIndex, totalThumbnailsToShow) {
  const isAR = Helper?.isAR({ media: thumbnail });
  const isAvataarAR = Helper?.isAvataarAR({ media: thumbnail });
  const isVideo = Helper?.isVideo({ media: thumbnail });
  const isThreesixty = Helper?.isImage360({ media: thumbnail });
  const isHotspot = Helper?.isHotspot({ media: thumbnail });
  const isCustomColor = Helper?.isCustomColor({ media: thumbnail });
  const isManualColor = Helper?.isManualColor({ media: thumbnail });
  const {
    id, url = '', videoStill = '', mediaStill = '', altText, brandLogo, rgb
  } = thumbnail;
  const alt = altText || id;

  let thumbnailUrl = url || '';
  if (isAR) {
    thumbnailUrl = mediaStill || '';
  } else if (isVideo) {
    thumbnailUrl = videoStill || '';
  } else if (isThreesixty) {
    thumbnailUrl = url ? `${url}?thumb&profile=400` : '';
  }

  const isLastThumbnail = thumbnailIndex === (totalThumbnailsToShow - 1);
  const isImagePath = (/^(?=.*\/productImages\/).*$/.test(thumbnailUrl));
  const reducedThumbnailUrl = thumbnailUrl ? thumbnailUrl.replace('_145.', '_100.') : '';
  const finalReducedThumbnailUrl = reducedThumbnailUrl
    ? reducedThumbnailUrl.replace('catalog/productImages/145', 'catalog/productImages/100')
    : '';

  let finalThumbnailUrl = thumbnailUrl;
  if (isVideo) {
    finalThumbnailUrl = thumbnailUrl ? thumbnailUrl.replace('/1280x720/match', '/88x50/match') : '';
  }

  return {
    alt,
    altText,
    brandLogo,
    finalReducedThumbnailUrl,
    finalThumbnailUrl,
    id,
    isAR,
    isAvataarAR,
    isCustomColor,
    isHotspot,
    isImagePath,
    isLastThumbnail,
    isManualColor,
    isThreesixty,
    isVideo,
    mediaStill,
    reducedThumbnailUrl,
    rgb,
    thumbnailUrl,
    url,
    videoStill
  };
}

export const getTypeFromAsset = (asset) => {
  if (Helper.isImage({ media: asset })) return 'IMAGE';
  if (Helper.isVideo({ media: asset })) return 'VIDEO';
  if (Helper.isImage360({ media: asset })) return 'THREESIXTY';
  if (Helper.isAR({ media: asset })) return 'AR';
  if (Helper.isHotspot({ media: asset })) return 'HOTSPOTS';
  if (asset?.type === 'CUSTOMER_IMAGE') return 'CUSTOMER_IMAGE';
  return 'IMAGE';
};

export const handleHideOverlay = (setOverlayOpened, onAvataarButtonClick) => {
  setOverlayOpened(false);
  if (onAvataarButtonClick) {
    onAvataarButtonClick();
  }
};

export const handleMainImageClick = ({
  thumbnailType,
  augmentedRealityIndex,
  selectedThumbnailIndex,
  mainImage,
  setOverlayOpened,
  organizedThumbs,
  originalThumbnails,
  setOverlaySelectedIndex,
  setOverlayInitialType,
  setOverlayInitialId,
  setSelectedThumbnailIndex
}) => {
  const payload = {
    eventName: 'expanded-view',
    primaryCategory: 'overlay'
  };
  thumbnailClick(payload);

  const selectedIndex = (thumbnailType === 'AVATAAR_AR') ? augmentedRealityIndex : selectedThumbnailIndex;
  const mediaType = Helper?.getMediaType(mainImage);

  const thumbnailPayload = {
    section: 'media',
    component: 'thumbnails',
    target: mediaType,
    position: 1 + selectedIndex,
    guid: mediaType === 'image' ? Helper?.getImageGuid(mainImage) : 'n/a'
  };

  publisherMediaClick(thumbnailPayload);
  const originalIndex = getOriginalIndexFromOrganized(
    selectedThumbnailIndex,
    organizedThumbs,
    originalThumbnails
  );
  setOverlaySelectedIndex(originalIndex);
  setOverlayInitialType(getTypeFromAsset(mainImage));
  setOverlayInitialId(mainImage?.id);
  setOverlayOpened(true);
  setSelectedThumbnailIndex(selectedIndex);

  if (typeof window !== 'undefined') {
    window.history.pushState({ page: 'MEDIAOVERLAY' }, null, '');
  }
};

export const handleThumbnailEvent = ({
  thumbnailList,
  images,
  thumbnail,
  eventType,
  totalThumbnailsToShow,
  originalThumbnailCount,
  setSelectedThumbnailIndex,
  setMainImage,
  setOverlayOpened,
  setOverlayInitialType,
  setOverlayInitialId,
  isOverlayOnClickEnabled
}) => {
  const index = thumbnailList.findIndex((t) => t.id === thumbnail.id);
  const totalCount = originalThumbnailCount || thumbnailList.length;

  switch (eventType) {
    case 'click': {
      if (index === -1) return null;
      setMainImage(images[index]);
      setSelectedThumbnailIndex(index);

      const mediaType = Helper?.getMediaType(thumbnail);
      const isInteractiveMedia = Helper?.isVideo({ media: thumbnail })
                                || Helper?.isAR({ media: thumbnail })
                                || Helper?.isImage360({ media: thumbnail })
                                || Helper?.isHotspot({ media: thumbnail })
                                || Helper?.isCustomColor({ media: thumbnail })
                                || Helper?.isManualColor({ media: thumbnail });

      if (isOverlayOnClickEnabled) {
        const overlayType = isInteractiveMedia ? getTypeFromAsset(thumbnail) : 'IMAGE';
        if (setOverlayInitialType) setOverlayInitialType(overlayType);
        if (setOverlayInitialId) setOverlayInitialId(thumbnail.id);
        setOverlayOpened(true);
        const expandedPayload = {
          eventName: 'expanded-view',
          primaryCategory: 'overlay'
        };
        thumbnailClick(expandedPayload);
      } else {
        const isLastThumbnail = totalThumbnailsToShow && index === (totalThumbnailsToShow - 1);

        if (isLastThumbnail && totalCount > totalThumbnailsToShow) {
          if (setOverlayInitialType) setOverlayInitialType('IMAGE');
          if (setOverlayInitialId) setOverlayInitialId(thumbnail.id);
          const payload = {
            eventName: 'expanded-view',
            primaryCategory: 'overlay'
          };
          thumbnailClick(payload);
          setOverlayOpened(true);
        }

        if (isInteractiveMedia) {
          const type = getTypeFromAsset(thumbnail);
          if (setOverlayInitialType) setOverlayInitialType(type);
          if (setOverlayInitialId) setOverlayInitialId(thumbnail.id);
          setOverlayOpened(true);
          const expandedPayload = {
            eventName: 'expanded-view',
            primaryCategory: 'overlay'
          };
          thumbnailClick(expandedPayload);
        }
      }

      const payload = {
        section: 'media',
        component: 'thumbnails',
        target: mediaType,
        position: index + 1,
        guid: mediaType === 'image' ? Helper?.getImageGuid(thumbnail) : 'n/a'
      };
      if (typeof publisherMediaClick === 'function') {
        publisherMediaClick(payload);
      }
      break;
    }
    case 'mouseenter': {
      if (index !== -1) {
        setMainImage(images[index]);
        setSelectedThumbnailIndex(index);
      }
      break;
    }
    default: break;
  }
  return null;
};

export const organizeThumbnails = (thumbnailList = [], maxThumbnails = 7) => {
  if (!Array.isArray(thumbnailList) || thumbnailList.length === 0) {
    return [];
  }

  const images = thumbnailList.filter((item) => Helper.isImage({ media: item }) && !Helper.isHotspot({ media: item }));
  const hotspots = thumbnailList.filter((item) => Helper.isHotspot({ media: item }));
  const interactiveNonHotspots = thumbnailList.filter((item) => !Helper.isImage({ media: item })
    && !Helper.isHotspot({ media: item })
    && (Helper.isVideo({ media: item })
      || Helper.isAR({ media: item })
      || Helper.isImage360({ media: item })
      || Helper.isCustomColor({ media: item })
      || Helper.isManualColor({ media: item }))
  );

  const organized = [];

  for (let i = 0; i < Math.min(4, images.length) && organized.length < maxThumbnails; i++) {
    organized.push(images[i]);
  }

  const reserveForHotspot = hotspots.length > 0 && organized.length < maxThumbnails;
  const fillLimit = reserveForHotspot ? maxThumbnails - 1 : maxThumbnails;

  for (let i = 0; i < interactiveNonHotspots.length && organized.length < fillLimit; i++) {
    organized.push(interactiveNonHotspots[i]);
  }

  /* eslint-disable-next-line max-len */
  for (let i = organized.filter((x) => images.includes(x)).length; i < images.length && organized.length < fillLimit; i++) {
    organized.push(images[i]);
  }

  if (reserveForHotspot && organized.length < maxThumbnails) {
    organized.push(hotspots[0]);
  }

  return organized;
};

export const updatePageLayout = (media, setMainImage, setSelectedThumbnailIndex, setThumbnails) => {
  const { main = [], thumbnails: mediaThumbnails = [] } = media || {};
  setMainImage(main[0]);
  setSelectedThumbnailIndex(0);
  setThumbnails(mediaThumbnails);
};

export const updatePagePostSuperSkuHoverLayout = (
  media,
  selectedThumbnailIndex,
  setMainImage,
  setThumbnails
) => {
  const { main = [], thumbnails: mediaThumbnails = [] } = media || {};
  setMainImage(main[selectedThumbnailIndex]);
  setThumbnails(mediaThumbnails);
};

export const updatePageSuperSkuHoverLayout = (
  media,
  mainSuperSkuHover,
  selectedThumbnailIndex,
  setMainImage,
) => {
  const { main = [] } = media || {};
  const index = mainSuperSkuHover.findIndex((t) => t.type === main[selectedThumbnailIndex]?.type);
  const isImage = Helper?.isImage({ media: mainSuperSkuHover[index] });
  setMainImage((isImage && index > 0) ? mainSuperSkuHover[index] : mainSuperSkuHover[0]);
};
